/**
 * Recency signal for session-list rows.
 *
 * The workspace server exposes three timestamps on every session
 * entry:
 *
 *   - ``last_modified`` — raw ``st_mtime`` of the JSONL on disk.
 *     Bumped by *any* write, including metadata records the
 *     Claude CLI keeps for itself (``custom-title``,
 *     ``file-history-snapshot``, ``last-prompt``,
 *     ``compact-summary``) and synthetic ``<synthetic>`` model
 *     frames the CLI persists after upstream API failures. Used
 *     verbatim, this is what produces the "session pops back to
 *     the top with an unread dot even though I never touched it"
 *     bug — the canonical example being a session whose JSONL
 *     contains exactly one record, a ``custom-title`` from a
 *     rename.
 *
 *   - ``last_event_at`` — derived server-side: the timestamp of
 *     the most recent *real* ``user`` / non-synthetic
 *     ``assistant`` turn found in the JSONL tail. ``null`` is the
 *     server's explicit "scanned and there's no user-visible
 *     content here" answer; ``undefined`` means an older agent
 *     server didn't compute the field.
 *
 *   - ``created_at`` — when the session record was first written.
 *     Constant for the session's lifetime.
 *
 * The right rule is:
 *
 *   - ``last_event_at`` is a real number → that's the truth, use
 *     it.
 *   - ``last_event_at === null`` → server scanned and the JSONL
 *     contains only metadata / synthetic records. Sort by
 *     ``created_at`` so the row settles into the time bucket it
 *     was created in (or ``0`` when even ``created_at`` is
 *     missing) and never bubbles back when the CLI rewrites
 *     metadata.
 *   - ``last_event_at === undefined`` → older workspace server. Fall
 *     back to ``last_modified`` because that's the only signal
 *     available; the polluted-mtime case is a known accept-list
 *     for that vintage of server.
 *
 * Earlier this lived as a one-liner in :class:`SessionSidebar`
 * with ``s.last_event_at ?? s.last_modified ?? 0``, but
 * ``??`` collapses ``null`` and ``undefined`` together — so the
 * server's "no content" signal silently fell through to the
 * polluted ``last_modified`` and the bug came right back.
 * Pulling the function here lets us pin the four-way semantics
 * with unit tests.
 */

import type { AgentSessionEntry } from './api';

/**
 * Compute the "last user-visible activity" timestamp the sidebar
 * uses for sorting and unread comparison.
 *
 * :param session: One session entry from the workspace server.
 * :return: Epoch milliseconds of the most recent meaningful
 *     activity, or ``0`` when nothing is known.
 */
export function diskActivityAt(session: AgentSessionEntry): number {
  // ``null`` is the server's explicit "scanned, no user-visible
  // turn" signal — trust it and fall through to created_at so
  // metadata-only sessions sort by when they were created rather
  // than when their title or summary was last rewritten.
  if (session.last_event_at === null) {
    return session.created_at ?? 0;
  }
  // ``undefined`` means the workspace server didn't ship the field at
  // all (older build). Fall back to ``last_modified`` because
  // that's the only signal we have.
  if (session.last_event_at === undefined) {
    return session.last_modified ?? 0;
  }
  return session.last_event_at;
}

/**
 * Best-effort label timestamp for the row's "Updated …" copy.
 * Mirrors :func:`diskActivityAt` so the label stays in sync with
 * the recency we sort by — but tolerates a ``null`` / missing
 * value by returning ``null`` (the renderer hides the line in
 * that case rather than printing a misleading mtime).
 *
 * :param session: One session entry from the workspace server.
 * :return: Epoch milliseconds suitable for the "Updated …"
 *     label, or ``null`` when no meaningful timestamp exists.
 */
export function displayActivityAt(session: AgentSessionEntry): number | null {
  if (session.last_event_at === null) {
    return session.created_at ?? null;
  }
  if (session.last_event_at === undefined) {
    return session.last_modified ?? null;
  }
  return session.last_event_at;
}
